import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './main.scss';

interface RepertoireRow {
  cat: string;
  work: string;
  status: string;
  on: boolean;
}

interface ReplaceItem {
  code: string;
  num: string;
  title: string;
  desc: string;
  old: string;
}

const repertoire: RepertoireRow[] = [
  { cat: 'BWV 1004', work: 'Partita No. 2 — Chaconne', status: '연주', on: true },
  { cat: 'Op. 24', work: 'Sonata No. 5 “Spring”', status: '이번 시즌', on: true },
  { cat: 'Op. 61', work: 'Violin Concerto in D', status: '학습', on: false },
  { cat: 'Op. 27/3', work: 'Ballade, solo violin', status: '연주', on: false },
];

const strip = [
  { dt: '레퍼토리', dd: '곡 단위로 정리 · 연주 / 준비 / 학습 구분' },
  { dt: '이력', dd: '날짜 · 장소 · 역할 · 앙상블까지 기록' },
  { dt: '공유', dd: '링크 하나로 기획자 · 동료 · 관객에게' },
];

const replaces: ReplaceItem[] = [
  {
    code: 'REP', num: '01', title: '레퍼토리',
    desc: '연주 가능한 곡을 카탈로그 번호로 정리합니다. 곡마다 상태를 열어두면, 물어보지 않아도 답이 됩니다.',
    old: '“이 곡 해보셨어요?” 단체방에 묻기',
  },
  {
    code: 'ENG', num: '02', title: '연주 이력',
    desc: '날짜·장소·역할이 함께 남습니다. “2026년 금호아트홀”로도, “바흐 무반주”로도 다시 꺼낼 수 있습니다.',
    old: '손으로 고친 프로필 PDF v7',
  },
  {
    code: 'SCH', num: '03', title: '시즌 일정',
    desc: '다음 무대가 프로필 맨 위에 뜹니다. 이력서와 프로필을 가르는 건 결국, 살아 있는지 여부입니다.',
    old: '인스타 스토리 24시간',
  },
  {
    code: 'AUD', num: '04', title: '녹음 · 영상',
    desc: '프로그램을 보내기 전에 소리로 판단하게. 라이브와 스튜디오 녹음을 곡에 붙여둡니다.',
    old: '카톡으로 영상 파일 전송',
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function BrandMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.3" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.3" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

export default function Main() {

  const pageRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [waitMsg, setWaitMsg] = useState('');
  const [waitErr, setWaitErr] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);

  // 로그인 상태 확인
  useEffect(() => {
    axios.get('/api/auth/me')
      .then((res) => setUserName(res.data.user.name))
      .catch(() => setUserName(null));
  }, []);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout').catch(() => {});
    setUserName(null);
  };

  // 스크롤 시 등장 애니메이션
  useEffect(() => {
    const rvs = pageRef.current?.querySelectorAll('.rv');
    if (!rvs) return;
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      rvs.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
    rvs.forEach((el) => el.classList.add('in'));
  }, []);

  const handleWaitSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = email.trim();
    if (!EMAIL_REGEX.test(v)) {
      setWaitMsg('이메일 주소를 다시 확인해 주세요.');
      setWaitErr(true);
      emailRef.current?.focus();
      return;
    }
    setWaitMsg(`신청되었습니다. 순서가 되면 ${v}(으)로 안내드립니다.`);
    setWaitErr(false);
    setEmail('');
  };

  return (
    <div className="main-page" ref={pageRef}>

      <header className="nav">
        <div className="nav-in">
          <a className="brand" href="#top">
            <BrandMark size={24} />
            <span className="brand-txt">
              <span className="brand-kr">클래시커</span>
              <span className="brand-lat">CLASSICKER</span>
            </span>
          </a>
          <nav className="nav-links">
            <Link to="/intro">프로필</Link>
            <Link to="/profile">샘플 프로필</Link>
          </nav>
          <div className="nav-right">
            {userName ? (
              <>
                <Link className="link-quiet" to="/mypage">마이페이지</Link>
                <button className="link-quiet link-btn" type="button" onClick={handleLogout}>로그아웃</button>
              </>
            ) : (
              <>
                <Link className="link-quiet" to="/login">로그인</Link>
                <Link className="btn btn-primary" to="/signup">회원가입</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="top">

        {/* ---------------- hero ---------------- */}
        <section className="hero">
          <div className="wrap">
            <div className="staff" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></div>
            <div className="hero-grid">
              <div>
                <p className="eyebrow">연주자를 위한 프로필 플랫폼</p>
                <h1>당신이 연주할 수 있는 것을,<br />한 페이지에 <em>정리한다</em>.</h1>
                <p className="lede">
                  레퍼토리 128곡, 연주 이력 42회, 이번 시즌 일정까지.{' '}
                  <b>클래식은 이력서로 증명되지 않습니다.</b> 무엇을, 어디서, 누구와 연주했는지가
                  곡 단위로 남아야 합니다. 클래시커는 그 기록을 한 장의 프로필로 만듭니다.
                </p>
                <div className="hero-cta">
                  <Link className="btn btn-primary btn-lg" to={userName ? '/profile/new' : '/signup'}>프로필 만들기 — 무료</Link>
                  <Link className="btn btn-ghost btn-lg" to="/profile">샘플 프로필 보기</Link>
                </div>
                <p className="hero-note"><i></i>10분이면 채워집니다 · 악보 업로드는 선택</p>
              </div>

              <div className="spec-wrap">
                <div className="spec">
                  <div className="spec-top">
                    <div>
                      <div className="spec-name">윤서하</div>
                      <div className="spec-role">Violin · 앙상블 오르페우스</div>
                    </div>
                    <span className="spec-badge">공개 프로필</span>
                  </div>

                  <div className="spec-label"><span>레퍼토리</span><span>상태</span></div>
                  {repertoire.map((row) => (
                    <div className="spec-row" key={row.cat}>
                      <span className="spec-cat">{row.cat}</span>
                      <span className="spec-work">{row.work}</span>
                      <span className={`spec-st${row.on ? ' on' : ''}`}>{row.status}</span>
                    </div>
                  ))}

                  <div className="spec-foot">
                    <span className="spec-url">classicker.com/yoon.seoha</span>
                    <span>11.14 금호아트홀</span>
                  </div>
                </div>
                <span className="spec-tag">SPECIMEN — 예시 프로필</span>
              </div>
            </div>

            <dl className="hero-strip">
              {strip.map((s) => (
                <div key={s.dt}><dt>{s.dt}</dt><dd>{s.dd}</dd></div>
              ))}
            </dl>
          </div>
        </section>

        {/* ---------------- replaces ---------------- */}
        <section className="sec" id="what">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="sec-num">01</span>
              <h2>지금은 카톡과 PDF로 되어 있는 일</h2>
            </div>
            <p className="sec-desc rv">클래식 연주자의 이력을 증명하는 방식은 20년째 그대로입니다. 클래시커는 그 네 가지를 프로필 하나로 대체합니다.</p>

            <div className="rep-grid rv">
              {replaces.map((item) => (
                <div className="rep-cell" key={item.code}>
                  <div className="rep-code"><span>{item.code}</span><span>{item.num}</span></div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <div className="rep-old"><b>이전</b> — {item.old}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- paper ---------------- */}
        <section className="paper" id="paper">
          <div className="wrap">
            <div className="paper-head">
              <span>Programme Note</span>
              <span>No. 01</span>
            </div>
            <div className="paper-grid">
              <p className="paper-q">좋은 프로필은 이력서가 아니라<br /><em>프로그램</em>입니다.</p>
              <div className="paper-body">
                <p>연주회 프로그램은 연주자가 자기 이야기를 스스로 배열한 결과입니다. 무엇으로 시작해 어디서 멈추고, 인터미션 뒤에 무엇을 두는지 — 그 순서가 그 사람의 취향과 판단을 드러냅니다.</p>
                <p>그래서 클래시커의 프로필은 경력의 목록이 아니라 배열입니다. 곡을 쌓고, 순서를 정하고, 무대를 붙이면 페이지가 스스로 하나의 프로그램이 됩니다. 스펙보다 정확하고, SNS보다 오래 남습니다.</p>
                <div className="paper-sign">클래시커 · 2026</div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- final cta ---------------- */}
        <section className="final" id="join">
          <div className="wrap">
            <h2 className="rv">첫 프로필을 만들어 두세요.</h2>
            <p className="rv">베타 기간에는 프로필 생성과 공개가 무료입니다. 이메일을 남기면 순서대로 안내드립니다.</p>
            <form className="wait rv" onSubmit={handleWaitSubmit} noValidate>
              <input
                ref={emailRef}
                type="email"
                placeholder="이메일 주소"
                aria-label="이메일 주소"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="btn btn-primary" type="submit">신청하기</button>
            </form>
            <p className={`wait-msg${waitErr ? ' err' : ''}`} role="status">{waitMsg}</p>
          </div>
        </section>

      </main>

      <footer className="foot">
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-brand">
              <a className="brand" href="#top">
                <BrandMark size={22} />
                <span className="brand-txt"><span className="brand-kr">클래시커</span><span className="brand-lat">CLASSICKER</span></span>
              </a>
              <p>클래식을 연주하고 듣는 사람들의 프로필.</p>
            </div>
            <div className="foot-col">
              <h4>서비스</h4>
              <Link to="/intro">프로필</Link>
              <a href="#top">앙상블</a>
              <a href="#top">공연 일정</a>
            </div>
            <div className="foot-col">
              <h4>둘러보기</h4>
              <Link to="/profile">샘플 프로필</Link>
              <a href="#top">연주자 찾기</a>
              <a href="#top">이번 주 공연</a>
            </div>
            <div className="foot-col">
              <h4>고지</h4>
              <a href="#top">이용약관</a>
              <a href="#top">개인정보 처리방침</a>
              <a href="#top">문의</a>
            </div>
          </div>
          <div className="foot-bottom">
            <span>© 2026 CLASSICKER</span>
            <span>기획 프로토타입 화면입니다 · 인물·공연·수치는 예시</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
